import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Camera, LogOut, Pencil, X, Check, MapPin, Shield, Bell, Crown, Video } from "lucide-react";
import { PremiumBadge } from "@/components/PremiumBadge";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { VerifyPhotoDialog } from "@/components/VerifyPhotoDialog";
import { enablePush, disablePush, pushPermission } from "@/lib/push";
import { toast } from "sonner";

const CONTACT_INPUTS = [["phone_verified", "Phone / WhatsApp e.g. +44 7700 900123", "tel"], ["instagram_handle", "Instagram @handle", "text"],
  ["snapchat_handle", "Snapchat username", "text"], ["contact_email", "Email e.g. you@mail.com", "email"], ["facebook_handle", "Facebook username", "text"]];
const contactDraft = (u) => Object.fromEntries(CONTACT_INPUTS.map(([k]) => [k, u?.[k] || ""]));


const INTERESTS = ["Live music", "Football", "Galleries", "Travel", "Coffee", "Film", "Food", "Nightlife", "Art", "Fashion", "Running", "Theatre"];

export default function Profile() {
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [verifyOpen, setVerifyOpen] = useState(false);
  const fileRef = useRef();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draft, setDraft] = useState({ bio: user?.bio || "", interests: user?.interests || [], photos: user?.photos || [],
    ...contactDraft(user) });
  const [pushState, setPushState] = useState(pushPermission());

  const toggleAlerts = async () => {
    if (pushState === "granted") {
      await disablePush(); setPushState("default"); toast.success("Alerts turned off");
    } else {
      try { await enablePush(); setPushState("granted"); toast.success("Alerts on"); }
      catch (e) { toast.error(e.message === "denied" ? "Blocked in browser settings" : "Could not enable"); }
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, photos: [...d.photos, data.url] }));
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const toggle = (i) => setDraft((d) => ({ ...d, interests: d.interests.includes(i) ? d.interests.filter((x) => x !== i) : [...d.interests, i] }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/profile", draft); await refresh(); setEditing(false); toast.success("Profile updated"); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const doLogout = async () => { await logout(); navigate("/", { replace: true }); };
  if (!user) return null;
  const photos = (editing ? draft.photos : user.photos) || [];

  return (
    <div className="fade-up max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-4xl">Profile</h1>
        {!editing ? (
          <Button data-testid="edit-profile-btn" onClick={() => { setDraft({ bio: user.bio || "", interests: user.interests || [], photos: user.photos || [], ...contactDraft(user) }); setEditing(true); }} variant="outline" className="rounded-full border-white/15">
            <Pencil className="h-4 w-4 mr-2" /> Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button onClick={() => setEditing(false)} variant="ghost" className="rounded-full"><X className="h-4 w-4" /></Button>
            <Button data-testid="save-profile-btn" disabled={saving} onClick={save} className="btn-gold rounded-full"><Check className="h-4 w-4 mr-2" /> Save</Button>
          </div>
        )}
      </div>

      {/* Media cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
        {photos.map((p, i) => (
          <div key={i} className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-amber-500/20">
            <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
            {i === 0 && <span className="absolute top-2 left-2 chip text-[10px] px-2 py-0.5 rounded-full">Main</span>}
            {editing && (
              <button onClick={() => setDraft((d) => ({ ...d, photos: d.photos.filter((_, x) => x !== i) }))}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 flex items-center justify-center"><X className="h-4 w-4 text-white" /></button>
            )}
          </div>
        ))}
        {editing && photos.length < 6 && (
          <button data-testid="profile-add-photo" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="aspect-[3/4] rounded-2xl border-2 border-dashed border-amber-500/30 flex flex-col items-center justify-center gap-2 text-amber-400/80 hover:bg-amber-500/5">
            {uploading ? <div className="h-6 w-6 rounded-full border-2 border-amber-400/30 border-t-amber-400 animate-spin" /> : <><Camera className="h-6 w-6" /><span className="text-xs font-semibold">Add</span></>}
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} />
      </div>

      <div className="glass rounded-2xl p-6 mt-5">
        <h2 className="font-display font-black text-2xl flex items-center gap-2">{user.name}{user.age ? `, ${user.age}` : ""} {user.verified && <VerifiedBadge showLabel />} {user.premium && <PremiumBadge />}</h2>
        {user.city && <p className="text-sm text-slate-300 flex items-center gap-1.5 mt-1"><MapPin className="h-3.5 w-3.5" /> {user.city}, {user.country}</p>}

        <div className="mt-5">
          <Label className="text-slate-300">About</Label>
          {editing ? (
            <Textarea data-testid="profile-bio" value={draft.bio} onChange={(e) => setDraft({ ...draft, bio: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 rounded-xl" />
          ) : (
            <p className="text-sm text-slate-200 mt-1.5 leading-relaxed">{user.bio || "No bio yet."}</p>
          )}
        </div>

        <div data-testid="profile-contacts" className="mt-5">
          <Label className="text-slate-300">Contact card <span className="text-xs text-slate-500">(free to save · you choose what to share on each contact card)</span></Label>
          {editing ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1.5">
              {CONTACT_INPUTS.map(([k, ph, type]) => (
                <input key={k} data-testid={`profile-${k}`} type={type} value={draft[k]} maxLength={k === "phone_verified" ? 24 : k === "contact_email" ? 120 : 60} placeholder={ph}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  className="h-10 rounded-xl bg-white/5 border border-white/10 px-3 text-sm outline-none focus:border-amber-400/40" />
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-200 mt-1.5">{[user.phone_verified, user.instagram_handle && `IG ${user.instagram_handle}`, user.snapchat_handle && `Snap ${user.snapchat_handle}`, user.contact_email, user.facebook_handle && `FB ${user.facebook_handle}`].filter(Boolean).join(" · ") || "Not added yet."}</p>
          )}
        </div>

        <div className="mt-5">
          <Label className="text-slate-300 mb-2 block">Interests</Label>
          <div className="flex flex-wrap gap-2">
            {(editing ? INTERESTS : user.interests || []).map((i) => {
              const on = editing ? draft.interests.includes(i) : true;
              return editing ? (
                <button key={i} onClick={() => toggle(i)} className={`px-3.5 py-1.5 rounded-full text-sm border transition-all ${on ? "bg-pink-500/20 border-pink-400/50 text-pink-200" : "border-white/10 text-slate-300"}`}>{i}</button>
              ) : (
                <span key={i} className="chip px-3.5 py-1.5 rounded-full text-sm">{i}</span>
              );
            })}
            {!editing && (!user.interests || user.interests.length === 0) && <span className="text-sm text-slate-400">None yet</span>}
          </div>
        </div>
      </div>

      {!user.verified && (
        <button data-testid="profile-verify-btn" onClick={() => setVerifyOpen(true)}
          className="w-full glass rounded-2xl px-5 mt-5 py-4 flex items-center gap-3 border-sky-400/30 hover:border-sky-400/60 transition-colors text-left">
          <VerifiedBadge />
          <span className="flex-1"><span className="font-semibold block">Verify your photo</span><span className="text-xs text-slate-400">A 10-second selfie earns you the blue badge so matches trust it's really you</span></span>
        </button>
      )}
      <VerifyPhotoDialog open={verifyOpen} onOpenChange={setVerifyOpen} onVerified={refresh} />

      {(
      <button data-testid="profile-premium-link" onClick={() => navigate("/premium")}
        className="w-full glass rounded-2xl px-5 mt-5 py-4 flex items-center justify-between border-amber-500/30 hover:border-amber-500/50 transition-colors text-left">
        <span className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Crown className="h-5 w-5 text-amber-400" />
          </span>
          <span>
            <span className="block text-sm font-semibold gold-text">{user.premium ? "Premium active" : "Go Premium"}</span>
            <span className="block text-xs text-slate-400">{user.premium ? `${user.premium_plan === "weekly" ? "Weekly" : "Monthly"} pass · manage your plan` : "Longer calls, more messages, global events"}</span>
          </span>
        </span>
        <span className="text-xs font-semibold text-amber-300">{user.premium ? "Manage →" : "Unlock →"}</span>
      </button>
      )}
      <button data-testid="profile-setup-test-link" onClick={() => navigate("/setup-test")}
        className="w-full glass rounded-2xl px-5 mt-3 py-4 flex items-center justify-between hover:border-emerald-400/40 transition-colors text-left">
        <span className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center"><Video className="h-5 w-5 text-emerald-300" /></span>
          <span>
            <span className="block text-sm font-semibold">Test camera & mic</span>
            <span className="block text-xs text-slate-400">Check your setup before your next vibe check</span>
          </span>
        </span>
        <span className="text-xs font-semibold text-emerald-300">Test →</span>
      </button>

      <button data-testid="profile-alerts-toggle" onClick={toggleAlerts}
        className="w-full glass rounded-full px-5 mt-5 h-12 flex items-center justify-between hover:border-amber-500/40 transition-colors">
        <span className="flex items-center gap-2 text-sm text-slate-200"><Bell className="h-4 w-4 text-amber-400" /> Push alerts (interests, matches, plans)</span>
        <span className={`text-xs font-semibold ${pushState === "granted" ? "text-emerald-300" : "text-slate-400"}`}>{pushState === "granted" ? "On" : pushState === "denied" ? "Blocked" : "Off"}</span>
      </button>

      {user.role === "admin" && (
        <Button data-testid="profile-admin-link" onClick={() => navigate("/admin")} variant="outline" className="w-full rounded-full border-amber-500/30 text-amber-300 mt-3 h-12">
          <Shield className="h-4 w-4 mr-2" /> Admin panel
        </Button>
      )}

      <Button data-testid="logout-btn" onClick={doLogout} variant="ghost" className="w-full rounded-full text-pink-300 hover:text-pink-200 hover:bg-pink-500/10 mt-3 h-12">
        <LogOut className="h-4 w-4 mr-2" /> Log out
      </Button>
    </div>
  );
}
